import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Badge,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { projectsPrefix } from "../../../services/ProjectsServices";
import { useFetch } from "../../../services/hooks/useFetch";
import IMDRFComponent from "./IMDRFComponent";

import CloseIcon from "@mui/icons-material/Close";
import {
  PatientContext,
  PatientDispatchContext,
} from "../../../contexts/PatientContext.tsx";
import { ProjectContext } from "../../../contexts/ProjectContext";
import {
  RefreshType,
  useProjectRefresh,
} from "../../../services/hooks/useProjectRefresh.ts";
import CloseArrowTooltip from "../../CloseArrowTooltip";
import SnippetsEnabledTextField from "../../SnippetsEnabledTextField";
import AutoCompleteInput from "./AutoCompleteInput";
import {
  ATTRIBUTE_TYPES,
  PatientAttributeValueType,
  getCategoryByType,
  getCategoryIcon,
} from "./CategorizeUtils";

export default function IMDRFCategorize({
  anchorEl,
  anchorVertical = "bottom",
  open,
  onClose,
  attribute,
}: {
  anchorEl: Element | null;
  anchorVertical?: "bottom" | "top" | "center" | number;
  open: boolean;
  onClose: () => void;
  attribute?: PatientAttribute | null;
}) {
  const {
    id: projectId,
    imdrfEnabled,
    attributeTypes,
    isLocked,
  } = useContext(ProjectContext) ?? {};
  const { refreshProject } = useProjectRefresh();
  const patient = useContext(PatientContext);
  const dispatch = useContext(PatientDispatchContext);
  const [showImdrfSelect, setShowImdrfSelect] = useState(false);
  const [showImdrfSelectWindow, setShowImdrfSelectWindow] = useState(false);
  const [imdrfCode, setImdrfCode] = useState<
    ImdrfCodeWithOverrideDTO | ImdrfCode | null
  >(attribute?.imdrfCode ?? null);
  const [distinctValues, setDistinctValues] = useState<string[] | null>(null);
  const [reason, setReason] = useState(attribute?.reason ?? "");
  const [category, setCategory] = useState<PatientAttributeType | null>(
    getCategoryByType(attributeTypes ?? [], attribute?.attributeTypeId ?? "") ??
      null,
  );
  const [attributeValue, setAttributeValue] = useState<string | null>(
    attribute?.attributeValue ?? null,
  );
  const [attributeTypeId, setAttributeTypeId] = useState<string>(
    attribute?.attributeTypeId ?? "",
  );

  const [fieldData, setFieldData] = useState<PatientAttributeFieldData[]>(
    attribute ? structuredClone(attribute.fieldData) : [],
  );
  const {
    data: distinctFieldValues,
    fetch: fetchDistinctFieldValues,
    isLoading: distinctFieldValuesLoading,
  } = useFetch<Record<string, string[]>>();
  const [deleting, setDeleting] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [apiErrorMessage, setApiErrorMessage] = useState("");
  const actionRef = React.useRef<{ updatePosition: () => void }>(null);

  const {
    create: createCategorization,
    update: updateCategorization,
    delete: deleteCategorization,
    isLoading: patientLoading,
    error: categorizationError,
  } = useFetch<PatientAttribute[], PatientAttributeCURequest>();

  const { fetch: fetchDistinctValues, isLoading: distinctValuesLoading } =
    useFetch<string[]>();

  function setSelectedCode(code: ImdrfCodeWithOverrideDTO | ImdrfCode | null) {
    setImdrfCode(code);
    if (apiErrorMessage && apiErrorMessage.includes("IMDRF code")) {
      // if an overridden code was previously selected, clear the error state
      // on new choice to unlock Save
      setApiErrorMessage("");
    }
  }

  const { snippetsEnabledTextField, resetReasonField } =
    SnippetsEnabledTextField({
      reasonText: reason,
      labelText: "Enter Note/Reason",
      textChanged: setReason,
      snippetType: "ATTRIBUTE",
      disabled: attributeTypeId === "",
      attributeTypeId: attributeTypeId,
      snippetDialogWidthPx: 580,
      snippetDialogHeightPx: 220,
    });

  const handleShowImdrfSelect = useCallback(
    (cat: PatientAttributeType | null, subcat: string | null) => {
      let showControl = false;
      if (!cat) {
        setShowImdrfSelect(showControl);
      } else {
        if (imdrfEnabled) {
          if (cat.id === ATTRIBUTE_TYPES.ADVERSE_EVENT) {
            showControl = true;
          }
          if (cat.id === ATTRIBUTE_TYPES.PERFORMANCE && subcat === "false") {
            showControl = true;
          }
          if (
            cat.id === ATTRIBUTE_TYPES.DEVICE_DEFICIENCY &&
            subcat === "true"
          ) {
            showControl = true;
          }
        }
        setShowImdrfSelect(showControl);
      }
    },
    [imdrfEnabled],
  );

  function handleFieldDataInputChange(
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | React.SyntheticEvent
      | { target: { value: string; name: string } }
      | null,
  ) {
    if (!event) {
      return;
    }
    const customField = event.target;
    const fieldValue = "value" in customField ? customField.value : "";
    const attributeTypeFieldId = "name" in customField ? customField.name : "";
    const newFieldData = [...fieldData];
    let fieldToUpdate = newFieldData.find(
      (f) => f.attributeTypeFieldId === attributeTypeFieldId,
    );

    if (!fieldToUpdate) {
      fieldToUpdate = {
        id: null,
        attributeId: attribute?.id ?? null,
        attributeTypeFieldId: attributeTypeFieldId,
        fieldValue: fieldValue,
      };
      newFieldData.push(fieldToUpdate);
    } else {
      fieldToUpdate.fieldValue = fieldValue;
    }

    setFieldData(newFieldData);
  }

  const handleClose = useCallback(
    (closeModalWhenDone?: boolean) => {
      setShowImdrfSelect(false);
      setShowImdrfSelectWindow(false);
      setImdrfCode(null);
      setDistinctValues(null);
      setReason("");
      setCategory(null);
      setAttributeValue(null);
      setAttributeTypeId("");
      setFieldData([]);
      resetReasonField();
      setApiErrorMessage("");
      if (closeModalWhenDone) {
        onClose();
      }
    },
    [onClose, resetReasonField],
  );

  const handleClosePopover = useCallback(() => {
    handleClose(true);
  }, [handleClose]);

  function handleCategoryChange(
    event: React.SyntheticEvent,
    value: PatientAttributeType | null,
  ) {
    const attributeTypeId = value?.id;
    if (
      !attributeTypeId ||
      (event.type === "change" &&
        (event.target as HTMLInputElement).value === "" &&
        category)
    )
      return; // ignore clearing search text when option is selected
    const newCategory = getCategoryByType(
      attributeTypes ?? [],
      attributeTypeId,
    );
    if (newCategory) {
      categoryChanged(newCategory);
    } else {
      setCategory(null);
    }
  }

  const categoryChanged = useCallback(
    (newCategory: PatientAttributeType) => {
      let newSubcat: string | null = null;
      if (category !== newCategory) {
        setCategory(newCategory);
        setAttributeValue(newSubcat);
        setAttributeTypeId(newCategory.id);
      } else {
        newSubcat = attributeValue;
      }

      //For AEs we always show IMDRF selection
      //For the boolean types, we only show IMDRF selection when:
      //Performance or Clinical Benefit failure (false) or Device Deficient (true)
      setApiErrorMessage("");
      setShowImdrfSelectWindow(false);
      setDistinctValues(null);

      if (newCategory.allowRetrieveDistinct) {
        void fetchDistinctValues(
          `${projectsPrefix}/${projectId}/attributetypes/${newCategory.id}/values?search=`,
          {
            postResponseAction: (data) => {
              if (data) {
                setDistinctValues(data);
              }
            },
          },
        );
      }

      const fieldIdsToRetrieveDistinctValuesFor = [];

      for (const field of newCategory.attributeTypeFields ?? []) {
        if (field.allowRetrieveDistinct) {
          fieldIdsToRetrieveDistinctValuesFor.push(field.id);
        }
      }

      if (fieldIdsToRetrieveDistinctValuesFor.length > 0) {
        void fetchDistinctFieldValues(
          `${projectsPrefix}/${projectId}/attributetypes/${newCategory.id}/${fieldIdsToRetrieveDistinctValuesFor.join(",")}/values?search=`,
        );
      }

      handleShowImdrfSelect(newCategory, newSubcat);
    },
    [
      fetchDistinctValues,
      handleShowImdrfSelect,
      category,
      attributeValue,
      projectId,
      setAttributeTypeId,
      fetchDistinctFieldValues,
    ],
  );

  function handleChangeSub(event: { target: { value: string } }) {
    const newSubcat = event.target.value;
    subcatChanged(newSubcat);
  }

  function subcatChanged(newSubcat: string) {
    setAttributeValue(newSubcat);
    if (
      category &&
      (category.id === ATTRIBUTE_TYPES.CLINICAL_BENEFIT ||
        (category.id === ATTRIBUTE_TYPES.PERFORMANCE && newSubcat === "true") ||
        (category.id === ATTRIBUTE_TYPES.DEVICE_DEFICIENCY &&
          newSubcat === "false"))
    ) {
      if (attribute) {
        attribute.imdrfCode = null as unknown as ImdrfCode;
      }
      setImdrfCode(null);
      setShowImdrfSelectWindow(false);
    }
    handleShowImdrfSelect(category, newSubcat);
  }

  function handleBody(): PatientAttribute {
    let code: ImdrfCodeWithOverrideDTO | ImdrfCode | null = imdrfCode;
    let selectedCode: ImdrfCodeWithOverrideDTO | undefined = undefined;

    if (
      code &&
      (code as ImdrfCodeWithOverrideDTO).isOverridden &&
      (code as ImdrfCodeWithOverrideDTO).overrideCode
    ) {
      code = (imdrfCode as ImdrfCodeWithOverrideDTO).overrideCode;
      selectedCode = imdrfCode as ImdrfCodeWithOverrideDTO;
    }
    if (code && "children" in code && code.children) {
      (code as ImdrfCodeWithOverrideDTO).children = undefined;
    }
    if (selectedCode && selectedCode.children) {
      selectedCode.children = undefined;
    }

    const newFieldData: PatientAttributeFieldData[] = [];
    if (fieldData && fieldData.length > 0) {
      fieldData.forEach((f) => {
        if (f.fieldValue && f.fieldValue !== "") {
          newFieldData.push(f);
        }
      });
    }

    return {
      id: attribute?.id ?? "",
      patientId: attribute?.patientId ?? "",
      attributeValue: (attributeValue ?? "") as string,
      attributeTypeId: category!.id,
      imdrfCode: code as ImdrfCode,
      selectedImdrfCode: selectedCode as ImdrfCode,
      reason: reason,
      createdBy: attribute?.createdBy ?? "",
      createdAt: attribute?.createdAt ?? "",
      modifiedBy: attribute?.modifiedBy ?? "",
      modifiedAt: attribute?.modifiedAt ?? "",
      fieldData: newFieldData,
    };
  }

  function parseApiErrorMessage(apiError: {
    message?: string;
    detail?: string;
  }) {
    if (!apiError && !(apiError as { message?: string }).message) return "";
    if ((apiError as { detail?: string }).detail)
      return (apiError as { detail: string }).detail;

    const errorMessage = apiError.message ?? "";
    return errorMessage.substring(
      errorMessage.indexOf('"') + 1,
      errorMessage.lastIndexOf('"'),
    );
  }

  function onSubmit(event: React.MouseEvent, closeModalWhenDone: boolean) {
    event.preventDefault();
    const attributeData = handleBody();
    if (attributeData.reason.trim() === "") {
      return;
    }
    submitAndClose(attributeData, closeModalWhenDone);
  }

  function submitAndClose(
    attributeData: PatientAttribute,
    closeModalWhenDone: boolean,
  ) {
    const attributeRequest: PatientAttributeCURequest = {
      id: undefined,
      patientId: undefined,
      attributeTypeId: attributeData.attributeTypeId,
      attributeValue: attributeData.attributeValue,
      imdrfCodeId: attributeData.imdrfCode?.id,
      selectedImdrfCodeId: attributeData.selectedImdrfCode?.id,
      reason: attributeData.reason,
      fieldData: attributeData.fieldData,
    };

    if (!attribute || !attribute?.id) {
      void createCategorization({
        path: `${projectsPrefix}/${projectId}/patients/${patient?.id}/attributes`,
        body: attributeRequest,
        postResponseAction: (data) => {
          if (data) {
            dispatch({
              type: "patientAttributeUpdate",
              attributes: data,
            });
            refreshProject(RefreshType.ATTRIBUTE_TYPES);
            handleClose(closeModalWhenDone);
          }
        },
      });
    } else {
      //patientId is required for updates, but not allowed for create in the API
      attributeRequest.patientId = patient?.id;
      attributeRequest.id = attributeData.id;
      void updateCategorization({
        path: `${projectsPrefix}/${projectId}/patients/${patient?.id}/attributes/${attribute.id}`,
        body: attributeRequest,
        postResponseAction: (data) => {
          if (data) {
            dispatch({
              type: "patientAttributeUpdate",
              attributes: data,
            });
            refreshProject(RefreshType.ATTRIBUTE_TYPES);
            handleClose(closeModalWhenDone);
          }
        },
      });
    }
  }

  function onDelete(event: React.MouseEvent) {
    event.preventDefault();
    deleteAndClose();
  }

  function deleteAndClose() {
    void deleteCategorization({
      path: `${projectsPrefix}/${projectId}/patients/${patient?.id}/attributes/${attribute?.id}`,
      body: deleteReason,
      postResponseAction: (data) => {
        if (data) {
          dispatch({
            type: "patientAttributeUpdate",
            attributes: data,
          });
          refreshProject(RefreshType.ATTRIBUTE_TYPES);
          handleClose(true);
        }
      },
    });
  }

  function showAutoComplete(category: PatientAttributeType | null) {
    if (!category) return false;
    if (
      category &&
      category.id === ATTRIBUTE_TYPES.ADVERSE_EVENT &&
      imdrfEnabled
    )
      return false;
    return (
      category &&
      category.valueType === PatientAttributeValueType.STRING &&
      category.allowRetrieveDistinct
    );
  }

  function showTextInput(category: PatientAttributeType | null) {
    if (!category) return false;
    if (
      category &&
      category.id === ATTRIBUTE_TYPES.ADVERSE_EVENT &&
      imdrfEnabled
    )
      return false;
    return (
      category &&
      category.valueType === PatientAttributeValueType.STRING &&
      !category.allowRetrieveDistinct
    );
  }

  const getSubmitDisabled = useCallback(() => {
    if (isLocked) return true;
    if (patientLoading || distinctValuesLoading || distinctFieldValuesLoading)
      return true;
    if (!category) return true;
    if (
      attributeValue === null &&
      category.valueType !== null &&
      category.id !== ATTRIBUTE_TYPES.ADVERSE_EVENT
    )
      return true;
    if (
      category.valueType === PatientAttributeValueType.NUMBER &&
      isNaN(parseFloat(attributeValue ?? ""))
    )
      return true;
    if (!reason || reason.trim() === "") return true;
    if (showImdrfSelect && !imdrfCode) return true;
    for (const field of category.attributeTypeFields ?? []) {
      const fieldDataForField = fieldData.find(
        (value) => value.attributeTypeFieldId === field.id,
      );
      if (
        field.required &&
        (!fieldDataForField || !fieldDataForField.fieldValue)
      ) {
        return true;
      }
    }
    return !!(apiErrorMessage && apiErrorMessage !== "");
  }, [
    patientLoading,
    distinctValuesLoading,
    category,
    attributeValue,
    reason,
    showImdrfSelect,
    imdrfCode,
    apiErrorMessage,
    isLocked,
    distinctFieldValuesLoading,
    fieldData,
  ]);

  const getDeleteDisabled = useCallback(() => {
    return deleteReason.trim() === "" || patientLoading;
  }, [deleteReason, patientLoading]);

  //Error handling
  useEffect(() => {
    if (categorizationError) {
      setApiErrorMessage(
        parseApiErrorMessage(
          categorizationError as { message?: string; detail?: string },
        ),
      );
    }
  }, [categorizationError]);

  //This is to load an attribute for editing if one is passed in
  useEffect(() => {
    if (attribute && attribute.attributeTypeId) {
      const cat = getCategoryByType(
        attributeTypes ?? [],
        attribute.attributeTypeId,
      );
      if (cat) {
        setCategory(cat);
        categoryChanged(cat);
      }
    }
  }, [attribute, attributeTypes, categoryChanged]);

  function renderCustomStringField(field: PatientAttributeTypeField) {
    if (
      field &&
      field.valueType === PatientAttributeValueType.STRING &&
      field.allowRetrieveDistinct
    ) {
      return (
        <Autocomplete
          fullWidth
          value={
            fieldData.find((f) => f.attributeTypeFieldId === field.id)
              ?.fieldValue ?? ""
          }
          onInputChange={handleFieldDataInputChange}
          options={(distinctFieldValues && distinctFieldValues[field.id]) || []}
          id={field.id}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                className={"categorize-modal-textfield"}
                label={field.label ?? "Missing Label"}
                name={field.id}
                placeholder={`${field.label} value`}
                size="small"
                required={field.required ?? false}
              />
            );
          }}
        />
      );
    }

    return (
      <TextField
        className="categorize-modal-textfield"
        label={field.label ?? "Missing Label"}
        id={field.id}
        name={field.id}
        value={
          fieldData.find((f) => f.attributeTypeFieldId === field.id)
            ?.fieldValue ?? ""
        }
        placeholder={`${field.label} value`}
        slotProps={{
          input: {
            inputProps: { min: 0 },
          },
        }}
        size="small"
        required={field.required ?? false}
        onChange={handleFieldDataInputChange}
        fullWidth
      />
    );
  }

  function renderCustomBooleanField(field: PatientAttributeTypeField) {
    return (
      <FormControl size="small" fullWidth>
        <InputLabel>
          {field.label
            ? `${field.label}${field.required ? " *" : ""}`
            : "Missing Label"}
        </InputLabel>
        <Select
          label={
            field.label
              ? `${field.label}${field.required ? " *" : ""}`
              : "Missing Label"
          }
          id={field.id}
          name={field.id}
          value={
            fieldData.find((f) => f.attributeTypeFieldId === field.id)
              ?.fieldValue ?? ""
          }
          onChange={handleFieldDataInputChange}
          variant="outlined"
        >
          <MenuItem key="None" value="">
            None
          </MenuItem>
          {field.optionValues?.split(",").map((optionValue, index) => {
            return (
              <MenuItem
                key={optionValue}
                value={index === 0 ? "true" : "false"}
              >
                {optionValue}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    );
  }

  function renderCustomSelectField(field: PatientAttributeTypeField) {
    return (
      <FormControl size="small" fullWidth>
        <InputLabel>
          {field.label
            ? `${field.label}${field.required ? " *" : ""}`
            : "Missing Label"}
        </InputLabel>
        <Select
          label={
            field.label
              ? `${field.label}${field.required ? " *" : ""}`
              : "Missing Label"
          }
          id={field.id}
          name={field.id}
          value={
            fieldData.find((f) => f.attributeTypeFieldId === field.id)
              ?.fieldValue ?? ""
          }
          onChange={handleFieldDataInputChange}
          variant="outlined"
        >
          <MenuItem key="None" value="">
            None
          </MenuItem>
          {field.optionValues?.split(",").map((optionValue) => {
            return (
              <MenuItem key={optionValue} value={optionValue}>
                {optionValue}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    );
  }

  function renderCustomNumberField(field: PatientAttributeTypeField) {
    return (
      <TextField
        className="categorize-modal-textfield"
        label={field.label ?? "Missing Label"}
        id={field.id}
        name={field.id}
        value={
          fieldData.find((f) => f.attributeTypeFieldId === field.id)
            ?.fieldValue ?? ""
        }
        placeholder={"0-n"}
        slotProps={{
          input: {
            inputProps: { min: 0 },
          },
        }}
        size="small"
        type="number"
        required={field.required ?? false}
        onChange={handleFieldDataInputChange}
        fullWidth
      />
    );
  }

  function renderCustomAttributeField(field: PatientAttributeTypeField) {
    switch (field.valueType) {
      case "STRING":
        return renderCustomStringField(field);
      case "BOOLEAN":
        return renderCustomBooleanField(field);
      case "NUMBER":
        return renderCustomNumberField(field);
      case "SELECT":
        return renderCustomSelectField(field);
      default:
        return;
    }
  }

  function renderCustomAttributeFields(
    attributeTypeFields: PatientAttributeTypeField[],
  ) {
    // If there are not many, render them each in their own row
    if (attributeTypeFields.length < 4) {
      return attributeTypeFields.map((field) => (
        <Stack
          direction="row"
          key={"CustomField_" + field.id}
          width={"100%"}
          alignItems={"center"}
        >
          {renderCustomAttributeField(field)}
        </Stack>
      ));
    }

    // Otherwise, render a long list two by two
    const result: React.ReactNode[] = [];
    for (let i = 0; i < attributeTypeFields.length; i += 2) {
      const left = attributeTypeFields[i];
      const right = attributeTypeFields[i + 1];
      result.push(
        <Stack
          key={"CustomField_row" + i / 2}
          direction="row"
          width={"100%"}
          alignItems={"center"}
          spacing={2}
        >
          {left && (
            <Stack
              direction="column"
              width={(right && "50%") || "100%"}
              key={"CustomField_" + left.id}
              alignItems={"center"}
            >
              {renderCustomAttributeField(left)}
            </Stack>
          )}
          {right && (
            <Stack
              direction="column"
              width={"50%"}
              key={"CustomField_" + right.id}
              alignItems={"center"}
            >
              {renderCustomAttributeField(right)}{" "}
            </Stack>
          )}
        </Stack>,
      );
    }
    return result;
  }

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={handleClosePopover}
      anchorOrigin={{
        vertical: anchorVertical,
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      sx={{ maxHeight: "80vh" }}
      action={actionRef}
    >
      <Stack
        className={"drawer-header"}
        direction="row"
        spacing={1}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Typography variant="h6" sx={{ marginTop: "10px", marginLeft: "16px" }}>
          {`Categorize Patient ${patient?.patientNumber} ${
            category ? `/ ${category?.name}` : ""
          }`}
        </Typography>
        <IconButton
          onClick={handleClosePopover}
          sx={{ "&:hover": { bgcolor: "action.hover" } }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>
      <Box overflow={"auto"} maxHeight={"calc(80vh - 112px)"}>
        <Stack
          direction="row"
          spacing={1}
          divider={<Divider orientation="vertical" flexItem />}
        >
          <Stack direction="column" spacing={1.5} className="categorize-modal">
            <FormControl size="small" fullWidth>
              <Autocomplete<PatientAttributeType>
                value={category ?? null}
                onChange={handleCategoryChange}
                disabled={!!attribute || deleting}
                noOptionsText="No categories found"
                options={(attributeTypes ?? []).filter((at) => at.enabled)}
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
                getOptionLabel={(option) => option.name ?? ""}
                getOptionKey={(option) => option.id}
                renderValue={(value, _props, ownerState) =>
                  value && value.id === category?.id ? (
                    <Badge
                      id={category.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      {getCategoryIcon(category)}&nbsp;&nbsp;
                      {category.name}
                      {ownerState.focused && "  |"}
                    </Badge>
                  ) : (
                    <></>
                  )
                }
                renderOption={(props, option) => {
                  const {
                    "data-option-index": optionIndex,
                    key,
                    ...optionProps
                  } = props as typeof props & {
                    "data-option-index": number;
                    key: string;
                  };
                  const liProps = {
                    ...optionProps,
                    id: key,
                    "data-option-index": optionIndex,
                    tabIndex: optionIndex,
                  };
                  return (
                    <li {...liProps} key={key}>
                      <Box
                        id={key}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          width: "100%",
                        }}
                      >
                        {getCategoryIcon(option)}&nbsp;&nbsp;
                        {option.name}
                      </Box>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    error={!(apiErrorMessage === "")}
                    variant="outlined"
                    size="small"
                    label="Category Type"
                  />
                )}
              />
            </FormControl>

            {showAutoComplete(category) && (
              <AutoCompleteInput
                label="Sub-Category"
                state={attributeValue}
                setState={setAttributeValue}
                options={distinctValues ?? []}
                deleting={deleting}
                required
              />
            )}

            {showTextInput(category) && (
              <TextField
                label="Sub-Category"
                value={attributeValue ?? ""}
                onChange={(event) => {
                  setAttributeValue(event.target.value);
                }}
                disabled={deleting}
                fullWidth
                size="small"
                required
              />
            )}

            {category &&
              category.valueType === PatientAttributeValueType.BOOLEAN &&
              category.optionValues && (
                <FormControl required fullWidth size="small">
                  <InputLabel>Sub-Category</InputLabel>
                  <Select
                    value={attributeValue ?? ""}
                    size="small"
                    label="Sub-Category"
                    onChange={handleChangeSub}
                    disabled={deleting}
                    variant="outlined"
                  >
                    {category!.optionValues
                      .split(",")
                      .map((optionValue, index) => {
                        return (
                          <MenuItem
                            value={index === 0 ? "true" : "false"}
                            key={optionValue}
                          >
                            {optionValue}
                          </MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>
              )}

            {category &&
              category.valueType === PatientAttributeValueType.NUMBER && (
                <TextField
                  label="Sub-Category"
                  value={attributeValue ?? ""}
                  onChange={(event) => {
                    setAttributeValue(event.target.value);
                  }}
                  fullWidth
                  size="small"
                  type="number"
                  disabled={deleting}
                  required
                />
              )}

            {showImdrfSelect && (
              <>
                <Stack direction="row" width={"100%"} alignItems={"center"}>
                  <TextField
                    className={
                      "categorize-modal-textfield categorize-modal-imdrf" +
                      (imdrfCode ? " categorize-modal-black" : "")
                    }
                    label="IMDRF Code"
                    value={
                      imdrfCode
                        ? `${imdrfCode.code} - ${imdrfCode.term}`
                        : "No IMDRF Code Selected"
                    }
                    fullWidth
                    disabled
                    required
                    variant={"standard"}
                    slotProps={{
                      input: {
                        disableUnderline: true,
                      },
                    }}
                    multiline
                  />
                  <Button
                    onClick={() =>
                      setShowImdrfSelectWindow(!showImdrfSelectWindow)
                    }
                    variant="contained"
                    size="small"
                    fullWidth
                    endIcon={
                      showImdrfSelectWindow ? (
                        <KeyboardArrowLeft fontSize="medium" />
                      ) : (
                        <KeyboardArrowRight fontSize="medium" />
                      )
                    }
                    disabled={!category}
                  >
                    {showImdrfSelectWindow
                      ? "CLOSE IMDRF CODES"
                      : imdrfCode
                        ? "SEARCH FOR A DIFFERENT CODE"
                        : "SEARCH FOR IMDRF CODE"}
                  </Button>
                </Stack>
                <Stack direction="row" width={"100%"} alignItems={"center"}>
                  <TextField
                    className={
                      "categorize-modal-textfield categorize-modal-imdrf" +
                      (imdrfCode ? " categorize-modal-black" : "")
                    }
                    label="Non-IMDRF Code"
                    value={
                      imdrfCode
                        ? imdrfCode.nonImdrf
                          ? `${imdrfCode.nonImdrf}`
                          : "None"
                        : "This code will be added when an IMDRF code is selected"
                    }
                    fullWidth
                    disabled
                    variant={"standard"}
                    slotProps={{
                      input: {
                        disableUnderline: true,
                      },
                    }}
                    multiline
                  />
                </Stack>
              </>
            )}

            {category &&
              category.attributeTypeFields &&
              category.attributeTypeFields.length > 0 &&
              imdrfEnabled && (
                <Stack
                  direction="column"
                  key={"CustomFieldsWrapper"}
                  spacing={1.5}
                  width={"100%"}
                  alignItems={"center"}
                >
                  {category!.attributeTypeFields &&
                    renderCustomAttributeFields(category!.attributeTypeFields)}
                </Stack>
              )}
            <Stack
              direction="column"
              spacing={1}
              justifyContent={"space-between"}
              width={"100%"}
            >
              {snippetsEnabledTextField}
            </Stack>

            {apiErrorMessage && (
              <FormControl fullWidth>
                <Alert severity="error">{apiErrorMessage}</Alert>
              </FormControl>
            )}
            {deleting && (
              <Stack
                direction="column"
                spacing={1}
                justifyContent={"space-between"}
                width={"100%"}
              >
                <TextField
                  className="categorize-modal-delete-textfield"
                  name={"CategorizationDeleteReason"}
                  label="Enter Note/Reason for Deleting"
                  fullWidth
                  maxRows={4}
                  multiline
                  value={deleteReason ?? ""}
                  onChange={(event) => setDeleteReason(event.target.value)}
                  placeholder="Please enter a reason for deleting this categorization."
                />
              </Stack>
            )}
            {!deleting && (
              <Stack
                direction="row"
                spacing={1}
                justifyContent={"space-between"}
                width={"100%"}
              >
                {!!attribute && (
                  <CloseArrowTooltip
                    title={isLocked ? "This project is locked." : ""}
                  >
                    <span>
                      <Button
                        onClick={() => setDeleting(true)}
                        color="error"
                        variant="contained"
                        size="small"
                        endIcon={
                          patientLoading && <CircularProgress size="1.5rem" />
                        }
                        disabled={patientLoading || isLocked}
                      >
                        Delete
                      </Button>
                    </span>
                  </CloseArrowTooltip>
                )}
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent={"flex-end"}
                  width={"100%"}
                >
                  <Button
                    onClick={handleClosePopover}
                    variant="outlined"
                    size="small"
                  >
                    Cancel
                  </Button>
                  <CloseArrowTooltip
                    title={isLocked ? "This project is locked." : ""}
                  >
                    <span>
                      <Button
                        variant="contained"
                        onClick={(e) => {
                          onSubmit(e, true);
                        }}
                        size="small"
                        endIcon={
                          patientLoading && <CircularProgress size="1.5rem" />
                        }
                        disabled={getSubmitDisabled()}
                      >
                        Save
                      </Button>
                    </span>
                  </CloseArrowTooltip>
                  {!attribute && (
                    <Button
                      variant="contained"
                      onClick={(e) => {
                        onSubmit(e, false);
                      }}
                      size="small"
                      endIcon={
                        patientLoading && <CircularProgress size="1.5rem" />
                      }
                      disabled={getSubmitDisabled()}
                    >
                      Save and Add Another
                    </Button>
                  )}
                </Stack>
              </Stack>
            )}
            {deleting && (
              <Stack
                direction="row"
                spacing={1}
                justifyContent={"flex-end"}
                width={"100%"}
              >
                <Button
                  onClick={() => setDeleting(false)}
                  variant="outlined"
                  size="small"
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={(e) => onDelete(e)}
                  size="small"
                  endIcon={patientLoading && <CircularProgress size="1.5rem" />}
                  disabled={getDeleteDisabled()}
                >
                  Delete
                </Button>
              </Stack>
            )}
          </Stack>
          <Collapse
            orientation="horizontal"
            in={showImdrfSelectWindow}
            unmountOnExit
            timeout={0}
            onEntered={() => actionRef.current?.updatePosition()}
            onExited={() => actionRef.current?.updatePosition()}
          >
            <IMDRFComponent
              projectId={projectId ?? ""}
              imdrfAnnex={category?.imdrfAnnex ?? ""}
              selectedCode={imdrfCode}
              setSelectedCode={setSelectedCode}
            />
          </Collapse>
        </Stack>
      </Box>
    </Popover>
  );
}
